import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage, Input, Textarea } from '#components/atoms/forms/index.ts';
import { ConfirmChangesBanner } from '#components/patterns/confirmations/confirm-changes-banner/ConfirmChangesBanner.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { SelectControlled } from '#components/patterns/forms/select/select/SelectControlled.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import { useCapabilities } from '#global/context/capabilities/useCapabilities.ts';
import { checkIsFormValid } from '#util/validation/checkIsFormValid.ts';
import { contactUsFormSchema, REASONS, type ContactUsFormData } from './contact-us-form-schema.ts';
import { ORG_ID_FIELD_ID, REASON_FIELD_ID, useContactUsMutation } from './ContactUsForm.queries.ts';

const DEFAULT_VALUES = {
  reason: 'support',
  name: '',
  email: '',
  message: '',
} satisfies ContactUsFormData;

const SUBJECT_LENGTH = 60;

type Props = {
  email: string;
  orgId: string;
  onSuccess: () => void;
  className?: string;
};

export function ContactUsForm({ className, email, orgId, onSuccess }: Props) {
  const defaultValues = { ...DEFAULT_VALUES, email } satisfies ContactUsFormData;
  const formContext = useForm({
    resolver: zodResolver(contactUsFormSchema),
    defaultValues: defaultValues,
  });
  const { register, control, handleSubmit, setError, clearErrors, formState, reset } = formContext;
  const { isSubmitting, errors, isSubmitted, isDirty } = formState;
  const isValid = checkIsFormValid(errors);
  const contactUs = useContactUsMutation();
  const { capabilities } = useCapabilities();

  const onSubmit = handleSubmit(async (data) => {
    clearErrors('root.global');

    // Zendesk makes us send a subject, but we don't want to ask the user to make one, so just
    // truncate the start of the message if necessary
    const subject = data.message.length > SUBJECT_LENGTH ? `${data.message.slice(0, SUBJECT_LENGTH)}…` : data.message;

    // https://developer.zendesk.com/api-reference/ticketing/tickets/ticket-requests/#json-format
    const requestBody = JSON.stringify({
      request: {
        requester: {
          name: data.name,
          email: data.email,
        },
        custom_fields: [
          { id: ORG_ID_FIELD_ID, value: orgId },
          { id: REASON_FIELD_ID, value: data.reason },
        ],
        priority: capabilities?.prioritySupport ? 'high' : 'normal',
        subject,
        comment: { body: data.message },
      },
    });
    try {
      await contactUs.mutateAsync(requestBody);
      onSuccess();
    } catch (err) {
      if (err instanceof ValidationError) {
        handleServerFormError(err, ({ path, message }) => {
          switch (path) {
            case 'name':
            case 'email':
            case 'reason':
            case 'message':
              setError(path, { message });
              return true;
            case undefined:
              setError('root.global', {
                message: 'Unable to create ticket, please try again or email support@defined.net.',
              });
              return true;
            default:
              setError('root.global', {
                message: 'Unable to create ticket, please try again or email support@defined.net.',
              });
              return false;
          }
        });
      } else {
        trackError(err);
        setError('root.global', { message: 'Unable to create ticket, please try again or email support@defined.net.' });
      }
    }
  });

  function onCancel() {
    reset();
  }

  return (
    <form method="post" id="contact-form" onSubmit={onSubmit} className={className}>
      <Spacer direction="vertical" size="wide" className="mb-24">
        <p className="p-md">
          We’d love to hear from you, for any reason. If you have a question, a suggestion, feedback, or an issue, let
          us know and we’ll get back to you as soon as we can.
        </p>
        {errors.root?.global && <ErrorMessage className="mb-24">{errors.root.global.message}</ErrorMessage>}
        <SelectControlled
          className="width-100"
          control={control}
          name="reason"
          label="Reason for contact"
          placeholder="Select…"
          errorMessage={errors.reason?.message}
          renderValue={(v) => REASONS[v]}
          renderOptions={({ onChange }) => (
            <>
              {Object.entries(REASONS).map(([value, label]) => (
                <SelectItem key={value} value={value} onClick={() => onChange(value)}>
                  {label}
                </SelectItem>
              ))}
            </>
          )}
        />
        <Field label="Name">
          <Input
            className="width-100"
            maxLength={256}
            hasError={Boolean(errors.name)}
            {...register('name')}
            enablePWManagers={true}
          />
          {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
        </Field>
        <Field label="Email address">
          <Input
            className="width-100"
            maxLength={500}
            hasError={Boolean(errors.email)}
            {...register('email')}
            enablePWManagers={true}
          />
          {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
        </Field>
        <Field label="How can we help you?">
          <Textarea
            className="width-100"
            resizable="vertical"
            hasError={Boolean(errors.message)}
            {...register('message')}
          />
          {errors.message && <ErrorMessage>{errors.message.message}</ErrorMessage>}
        </Field>
      </Spacer>
      <ConfirmChangesBanner
        isOpen={isDirty}
        title={
          isSubmitted && !isValid ?
            <span>
              Unable to submit. Please update and try again or email{' '}
              <a href="mailto:support@defined.net">support@defined.net</a>.
            </span>
          : ''
        }
        primaryButton={
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            Submit
          </Button>
        }
        secondaryButton={
          <Button onClick={onCancel} variant="link">
            Reset
          </Button>
        }
      />
    </form>
  );
}
